'use client';

import { useAuthUserType } from '@hooks/use-auth-user-type';
import { v2Links } from '@utils/navigation/links';
import { safeInternalPath } from '@utils/navigation/safe-redirect';
import { sendVerificationCode } from '@v2/action';
import {
  AlertToast,
  AlertTriangle,
  Button,
  Google,
  Input,
  Typography,
} from '@v2/ui';
import { HorizontalDividerWithText } from '@v2/ui/Divider/HorizontalDividerWithText';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState, useTransition } from 'react';
import toast from 'react-hot-toast';
import { useSignin } from '../use-auth';

export const AUTH_PROVIDERS_URLS = {
  google: {
    path: `${process.env.SERVER_URL}/auth/google`,
    text: 'with Google',
  },
  facebook: {
    path: `${process.env.SERVER_URL}/auth/facebook`,
  },
};

export function SigninFlow() {
  const router = useRouter();
  const userType = useAuthUserType();
  const searchParams = useSearchParams();
  const redirectUrl =
    safeInternalPath(searchParams?.get('redirectUrl')) ?? undefined;

  const [isPending, startTransition] = useTransition();
  const [agencyPromptEmail, setAgencyPromptEmail] = useState<string | null>(
    null
  );

  const getAgencyLoginUrl = (email: string) => {
    const params = new URLSearchParams({ email, userType: 'agency' });
    if (redirectUrl) params.set('redirectUrl', redirectUrl);

    return `${v2Links.login}?${params.toString()}`;
  };

  const [{ formik, loading }] = useSignin({
    onSubmitCallback: async (data) => {
      const email = formik.values.email;
      const values = {
        ...formik.values,
        email,
      };
      const getAuthUrl = (path: string) => {
        const params = new URLSearchParams({ email, userType });
        if (redirectUrl) params.set('redirectUrl', redirectUrl);

        return `${path}?${params.toString()}`;
      };

      startTransition(async () => {
        // This email is signed up as an agent - direct them to the agency
        // sign-in instead of continuing (or treating this as a new signup).
        if (
          userType !== 'agency' &&
          typeof data === 'object' &&
          data.hasAgencyAccount
        ) {
          setAgencyPromptEmail(email);
          return;
        }

        if (
          data == 'User not found.' ||
          (typeof data === 'object' && data.status === 'partial')
        ) {
          userType === 'agency'
            ? router.push(`${v2Links.partnerSignup}`)
            : router.push(getAuthUrl(v2Links.completeAccount));
          return;
        }
        const res = await sendVerificationCode({ ...values, userType });

        if (typeof res === 'string' && res !== 'Success') {
          toast.custom(
            (t) => (
              <AlertToast
                {...t}
                title="Oops, something went wrong"
                content={res}
                icon={<AlertTriangle />}
              />
            ),
            { position: 'top-right' }
          );
          return;
        }

        if (res?.error) {
          toast.custom(
            (t) => (
              <AlertToast
                {...t}
                title="Oops, something went wrong"
                content={res.message}
                icon={<AlertTriangle />}
              />
            ),
            { position: 'top-right' }
          );
          return;
        }

        router.push(getAuthUrl(v2Links.confirmIdentity));
      });
    },
  });
  // Guard on userType too: navigating to the agency-flavored login page
  // (via the button below) re-renders this same component without
  // remounting it, so stale local state alone can't be trusted here.
  if (agencyPromptEmail && userType !== 'agency') {
    return (
      <div className="flex flex-col items-center gap-y-4 text-center">
        <Typography size="tmd" className="text-gray-700">
          This email is signed up as an agent, please sign in on the Sports
          Travel Agent Network
        </Typography>
        <Link href={getAgencyLoginUrl(agencyPromptEmail)} className="w-full">
          <Button variant="primary" size="xl" className="w-full">
            Sign in to Sports Travel Agent Network
          </Button>
        </Link>
        <Button
          type="button"
          variant="tertiary"
          size="xl"
          className="w-full"
          onClick={() => setAgencyPromptEmail(null)}
        >
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <>
      {userType === 'user' && (
        <>
          <div>
            <Link href={AUTH_PROVIDERS_URLS['google'].path}>
              <Button
                size="xl"
                variant="secondary"
                className="flex w-full items-center justify-center"
              >
                <div className="flex items-center">
                  <Google className="mr-[10px]" size={'20'} />
                  Sign in with Google
                </div>
              </Button>
            </Link>
            {/* <Link href={AUTH_PROVIDERS_URLS['facebook'].path}>
          <Button
            size="xl"
            variant="secondary"
            className="w-full flex items-center justify-center mt-3"
          >
            <div className="flex items-center ">
              <Facebook className="mr-[10px]" size={'20'} />
              Sign in with Facebook
            </div>
          </Button>
        </Link> */}
          </div>
          <HorizontalDividerWithText>OR</HorizontalDividerWithText>
        </>
      )}

      <div>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-y-3">
          <Input
            extent="md"
            required
            type="email"
            placeholder="Enter your email"
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            isError={formik.touched.email && Boolean(formik.errors.email)}
            errorText={formik.errors.email}
          />

          <Button
            name="signinButton"
            type="submit"
            variant="primary"
            size="xl"
            className="w-full"
            isLoading={loading || isPending}
          >
            Continue
          </Button>
        </form>
      </div>
    </>
  );
}
